import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, FormsModule } from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';
import { OnChangeFn, OnTouchedFn, provideCva } from '../cva.utils';

export type InputType = 'text' | 'number' | 'email' | 'password';
export type InputAppearance = 'outline' | 'underline';

@Component({
  selector: 'app-input',
  standalone: true,
  imports: [FormsModule, NgxMaskDirective],
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideCva(InputComponent)],
  host: {
    '[class.app-input-focused]': 'focused()',
    '[class.app-input-disabled]': 'disabled()',
    '[class.app-input-has-value]': 'hasValue()',
    '[class.app-input-error]': 'hasError()',
    '[class.app-input-underline]': 'appearance() === "underline"',
    '[class.app-input-has-icon-left]': '!!iconLeft()',
    '[class.app-input-has-icon-right]': '!!iconRight()',
    '[class.app-input-has-prefix]': '!!prefix()',
    '[class.app-input-has-suffix]': '!!suffix()',
    '[class.app-input-currency]': 'currency()',
  },
})
export class InputComponent implements ControlValueAccessor {
  readonly label = input<string>('');
  readonly hint = input<string>('');
  readonly placeholder = input<string>('');
  readonly type = input<InputType>('text');
  readonly appearance = input<InputAppearance>('outline');
  readonly inputId = input<string>('');
  readonly iconLeft = input<string>('');
  readonly iconRight = input<string>('');
  readonly prefix = input<string>('');
  readonly suffix = input<string>('');
  readonly helperText = input<string>('');
  readonly errorMessage = input<string>('');
  readonly required = input<boolean>(false);
  readonly mask = input<string>('');
  readonly dropSpecialCharacters = input<boolean>(true);
  readonly currency = input<boolean>(false);
  readonly numeric = input<boolean>(false);
  readonly decimals = input<number>(0);

  readonly inputPaddingLeft = computed(() => {
    const p = this.prefix();
    if (!p) {
      return null;
    }
    const iconGap = this.iconLeft() ? 24 : 0;
    const textWidth = Math.max(14, p.length * 8.5);
    return `${Math.ceil(textWidth + 20 + iconGap)}px`;
  });

  readonly inputPaddingRight = computed(() => {
    const s = this.suffix();
    if (!s) {
      return null;
    }
    const iconGap = this.iconRight() ? 24 : 0;
    const textWidth = Math.max(14, s.length * 8.5);
    return `${Math.ceil(textWidth + 20 + iconGap)}px`;
  });

  private readonly isNumeric = computed(() => this.currency() || this.numeric());

  readonly effectiveMask = computed(() =>
    this.isNumeric() ? `separator.${this.decimals()}` : this.mask(),
  );
  readonly effectivePrefix = computed(() => (this.currency() ? 'R$ ' : ''));
  readonly effectiveThousandSeparator = computed(() => (this.isNumeric() ? '.' : ''));
  readonly effectiveDropSpecialCharacters = computed(() =>
    this.isNumeric() ? true : this.dropSpecialCharacters(),
  );
  readonly effectiveInputType = computed<InputType | 'tel'>(() =>
    this.isNumeric() ? 'tel' : this.type(),
  );
  readonly effectiveInputMode = computed(() => (this.decimals() > 0 ? 'decimal' : 'numeric'));

  readonly blurred = output<void>();
  readonly enterKey = output<void>();

  readonly value = signal<string | number>('');
  readonly disabled = signal(false);
  readonly focused = signal(false);
  readonly touched = signal(false);

  readonly hasValue = computed(() => {
    const v = this.value();
    return v !== '' && v !== null && v !== undefined;
  });

  readonly hasError = computed(() => !!this.errorMessage());

  readonly displayMessage = computed(
    () => this.errorMessage() || this.helperText(),
  );

  readonly inputRef = viewChild<ElementRef<HTMLInputElement>>('inputEl');

  private onChange: OnChangeFn = () => {};
  private onTouched: OnTouchedFn = () => {};

  writeValue(value: string | number | null | undefined): void {
    if (this.isNumeric()) {
      this.value.set(this.toFixedDecimals(value));
      return;
    }

    this.value.set(value ?? '');
  }

  registerOnChange(fn: OnChangeFn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: OnTouchedFn): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  onModelChange(raw: string | number): void {
    this.value.set(raw);
    this.onChange(this.isNumeric() ? this.parseNumericValue(raw) : raw);
  }

  onFocus(): void {
    this.focused.set(true);
  }

  onBlur(): void {
    this.focused.set(false);
    const currentValue = this.value();
    if (this.isNumeric()) {
      const parsed = this.parseNumericValue(currentValue);
      this.value.set(this.toFixedDecimals(parsed));
      this.onChange(parsed);
    } else {
      this.onChange(currentValue);
    }
    if (!this.touched()) {
      this.touched.set(true);
      this.onTouched();
    }
    this.blurred.emit();
  }

  onEnterKey(event: Event): void {
    event.preventDefault();
    this.enterKey.emit();
  }

  focus(): void {
    this.inputRef()?.nativeElement.focus();
  }

  private toFixedDecimals(value: string | number | null | undefined): string | number {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const parsed = Number(value);
    if (Number.isNaN(parsed) || this.decimals() <= 0) {
      return value;
    }
    return parsed.toFixed(this.decimals());
  }

  private parseNumericValue(raw: string | number | null | undefined): number | null {
    if (raw === '' || raw === null || raw === undefined) {
      return null;
    }

    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  }
}
